import {
  VerificationEntityType,
  VerificationRequestType,
  VerificationStatus,
} from "~/types/enums";
import type { Tables } from "~/types/types.gen";
import { HttpMethod } from "~/utils/constants";

type EntityClaimEligibility = {
  entity_id: string;
  entity_type: VerificationEntityType;
  name: string | null;
  avatar_url: string | null;
  claimable: boolean;
  reason: "ENTITY_NOT_FOUND" | "ALREADY_CLAIMED" | "CLAIMABLE" | "GYM_MANAGED";
  active_claims_count: number;
};

export default function useVerificationsDatabase() {
  /**
   * Submit a new verification request
   */
  async function submitVerification(data: {
    entity_id: string;
    entity_type:
      | VerificationEntityType.FIGHTER
      | VerificationEntityType.GYM
      | VerificationEntityType.ORGANISATION;

    creator_account_data: {
      name: string;
      email: string;
    };
    role: string;
    request_type?: VerificationRequestType;
    entity_data?: any;
    notes?: string | null;
    documents: Array<{
      id?: string;
      url: string;
      name?: string;
      filename?: string;
      note?: string;
      size?: number;
      type?: string;
    }>;
  }) {
    try {
      return await $fetch("/api/verification/request", {
        method: HttpMethod.POST,
        body: data,
      });
    } catch (error) {
      usePosthog().logError(
        error as Error,
        "useVerificationsDatabase():submitVerification",
        "Error submitting verification request",
      );
      throw error;
    }
  }

  /**
   * Get a verification by ID
   */
  async function getVerification(id: string) {
    return await $fetch(`/api/verification/${id}`);
  }

  async function getVerificationRevisions(id: string) {
    return await $fetch(`/api/verification/${id}/revisions`);
  }

  /**
   * Cancel a pending verification (user)
   */
  async function cancelVerification(id: string) {
    try {
      return await $fetch(`/api/verification/${id}`, {
        method: HttpMethod.DELETE,
      });
    } catch (error) {
      usePosthog().logError(
        error as Error,
        "useVerificationsDatabase():cancelVerification",
        `Error cancelling verification with ID ${id}`,
      );
      throw error;
    }
  }

  /**
   * Get all verifications (admin only)
   */
  async function getVerifications(params?: {
    page?: number;
    page_size?: number;
    status?: string;
    entity_type?: string;
    request_type?: VerificationRequestType;
  }) {
    try {
      return await $fetch("/api/backoffice/verifications", {
        method: HttpMethod.GET,
        query: params,
      });
    } catch (error) {
      usePosthog().logError(
        error as Error,
        "useVerificationsDatabase():getVerifications",
        "Error fetching verifications",
      );
      throw error;
    }
  }

  /**
   * Update verification status (admin only)
   */
  async function updateVerificationStatus(
    id: string,
    data: {
      status:
        | VerificationStatus.APPROVED
        | VerificationStatus.REJECTED
        | VerificationStatus.MORE_INFO_REQUIRED;
      status_reason?: string;
    },
  ) {
    try {
      return await $fetch(`/api/backoffice/verification/${id}`, {
        method: HttpMethod.POST,
        body: data,
      });
    } catch (error) {
      usePosthog().logError(
        error as Error,
        "useVerificationsDatabase():updateVerificationStatus",
        "Error updating verification status",
      );
      throw error;
    }
  }

  /**
   * Check if entities have active verifications
   * @param entityIds - Array of entity IDs to check
   * @param getAllData - Whether to return full verification data or just boolean status
   * @returns Map of entityId to boolean indicating active verification status or full verification data
   */
  async function getVerificationStatus(
    entityIds: string[],
  ): Promise<Record<string, VerificationStatus>> {
    if (!entityIds || entityIds.length === 0) {
      return {};
    }

    try {
      const { data } = await useSupabaseClient()
        .from("verification_requests")
        .select("entity_id, status")
        .in("entity_id", entityIds)
        .eq("request_type", VerificationRequestType.VERIFICATION);

      const result: Record<string, VerificationStatus> = {};

      data?.forEach((verification) => {
        result[verification.entity_id] =
          verification.status as VerificationStatus;
      });

      return result;
    } catch (error) {
      usePosthog().logError(
        error as Error,
        "useVerificationsDatabase():hasActiveVerification",
        "Error checking active verifications",
      );

      // Return all NOT_VERIFIED on error
      return {};
    }
  }

  /**
   * Check if entities have active verifications
   * @param entityIds - Array of entity IDs to check
   * @returns Map of entityId to boolean indicating active verification status or full verification data
   */
  async function getActiveVerification(
    entityId: string,
  ): Promise<Tables<"verification_requests"> | null> {
    if (!entityId) {
      return null;
    }

    try {
      const { data } = await useSupabaseClient()
        .from("verification_requests")
        .select("*")
        .eq("entity_id", entityId)
        .eq("request_type", VerificationRequestType.VERIFICATION)
        .in("status", [
          VerificationStatus.PENDING,
          VerificationStatus.MORE_INFO_REQUIRED,
        ])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      return data;
    } catch (error) {
      usePosthog().logError(
        error as Error,
        "useVerificationsDatabase():getActiveVerification",
        "Error checking active verifications",
      );

      throw error;
    }
  }

  async function getLatestVerificationForEntity(
    entityId: string,
    requestType = VerificationRequestType.VERIFICATION,
  ): Promise<Tables<"verification_requests"> | null> {
    if (!entityId) {
      return null;
    }

    try {
      const { data } = await useSupabaseClient()
        .from("verification_requests")
        .select("*")
        .eq("entity_id", entityId)
        .eq("request_type", requestType)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      return data;
    } catch (error) {
      usePosthog().logError(
        error as Error,
        "useVerificationsDatabase():getLatestVerificationForEntity",
        "Error loading latest verification",
      );

      throw error;
    }
  }

  async function getClaimEligibility(params: {
    entity_id: string;
    entity_type: VerificationEntityType;
  }): Promise<EntityClaimEligibility> {
    return await $fetch("/api/entity-claims/eligibility", {
      method: HttpMethod.GET,
      query: params,
    });
  }

  async function getActiveClaim(): Promise<Tables<"verification_requests"> | null> {
    return await $fetch("/api/entity-claims/active", {
      method: HttpMethod.GET,
    });
  }

  return {
    submitVerification,
    getVerification,
    getVerificationRevisions,
    cancelVerification,
    getVerifications,
    updateVerificationStatus,
    getVerificationStatus,
    getActiveVerification,
    getLatestVerificationForEntity,
    getClaimEligibility,
    getActiveClaim,
  };
}
